import { API_URL, QUERY_KEYS } from "@/constants"
import { useMutation, useQuery } from "@tanstack/react-query"

interface ShareLink {
  _id: string
  token: string
  resourceType: string
  resourceId: string
  organizationId: string
  createdBy: string
  createdAt: string
  revokedAt?: string
}

interface CreateShareLinkRequest {
  resourceType: string
  resourceId: string
}

export const useCreateShareLink = (organizationId: string, token: string) => {
  return useMutation<ShareLink, Error, CreateShareLinkRequest>({
    mutationFn: async (data: CreateShareLinkRequest) => {
      const response = await fetch(`${API_URL}/organizations/${organizationId}/share-links`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Failed to create share link")
      }

      return response.json()
    },
  })
}

export const useGetShareLinks = (
  organizationId: string,
  token: string,
  resourceType?: string,
  resourceId?: string,
) => {
  const params = new URLSearchParams()
  if (resourceType) params.append("resourceType", resourceType)
  if (resourceId) params.append("resourceId", resourceId)
  const queryString = params.toString() ? `?${params.toString()}` : ""

  return useQuery<ShareLink[]>({
    queryKey: QUERY_KEYS.shareLinks(organizationId, resourceType, resourceId),
    queryFn: async () => {
      const response = await fetch(`${API_URL}/organizations/${organizationId}/share-links${queryString}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || "Failed to fetch share links")
      }

      return response.json()
    },
    enabled: !!organizationId && !!token,
  })
}

export interface ShareData {
  resourceType: string
  resourceId: string
  organizationId: string
  createdAt: string
  createdBy: {
    email: string
    name: string
  }
}

export const useResolveShareToken = (shareToken: string) => {
  return useQuery<ShareData>({
    queryKey: QUERY_KEYS.shareToken(shareToken),
    queryFn: async () => {
      const response = await fetch(`${API_URL}/share/${shareToken}`)

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Share link not found")
        } else if (response.status === 403) {
          throw new Error("Share link has expired or been revoked")
        } else {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(errorData.error || "Failed to load shared content")
        }
      }

      return response.json()
    },
    enabled: !!shareToken,
    retry: false, // Don't retry failed share token resolutions
  })
}
